import { useCallback, useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useStoryAvailability } from '@/lib/story-availability';
import '@/components/landing/immersive/hero.css';

// ToolCompass — the top-of-page tool switcher (user design 2026-07-22).
// One landing page, one layout, one set of rules — the compass swaps WHICH
// tool the whole page is about: content, accents, and the console's preselected
// workflow all follow the active tool. Swiping (drag, Tinder-style) or the
// arrows/labels moves between tools; the page transforms in place, no reload.
//
// THEME: each tool carries shadcn-style HSL triplets for --primary/--accent.
// HeroSection sets them on the page wrapper, so every hsl(var(--primary))
// consumer (orbs, buttons, chips) re-themes automatically. Corrido = the site's
// existing amber look (no override); TikTok story = official TikTok palette
// (cyan #25F4EE / razzmatazz #FE2C55 on black); ads = green/gold (product
// lock: the customer's exact pixels); rap = a muted slate teaser.
//
// FIVE STOPS (2026-09-24): the page now opens on a neutral WELCOME stop that
// lets a visitor pick what they came for, and the kids' STORYBOOK got its own
// stop — on a phone it used to sit eight screens below the corrido studio,
// where nobody found it. Only corrido / story / ads / rap drive the Director
// console (ConsoleToolId); welcome and storybook are landing-page stops only.
// Rap stays LAST so its "swipe the compass back to Corrido" copy stays true.

export type ConsoleToolId = 'corrido' | 'story' | 'ads' | 'rap';
export type ToolId = 'welcome' | ConsoleToolId | 'storybook' | 'chopbox';

const CONSOLE_TOOL_IDS: readonly string[] = ['corrido', 'story', 'ads', 'rap'];

export const isConsoleTool = (id: unknown): id is ConsoleToolId =>
  typeof id === 'string' && CONSOLE_TOOL_IDS.includes(id);

export interface ToolDef {
  id: ToolId;
  /** i18n key stem under `compass.` — never a literal, so the compass
      re-labels itself when the language toggle flips. */
  labelKey: string;
  /** shorter name for the dimmed neighbour labels beside the active one. */
  shortKey: string;
  tagKey: string;
  /** shadcn HSL triplet overrides; null = keep the site default (corrido). */
  primaryHsl: string | null;
  accentHsl: string | null;
  /** solid accent for small chrome (compass glow, active dot). A literal
      hex colour: the glow appends a hex alpha to it, and
      `hsl(var(--primary))44` is invalid CSS, which dropped the whole glow. */
  accentCss: string;
  /** optional --gradient-primary override (the .gradient-text headline). */
  gradient?: string;
  comingSoon?: boolean;
  /** studio-bus workflow id the console starts for this tool. */
  workflowId: 'corrido' | 'tiktok_story' | 'ads' | null;
}

export const TOOLS: ToolDef[] = [
  {
    // #F4BE52 is the site's own --primary amber (40 88% 64%).
    id: 'welcome', labelKey: 'compass.welcome', shortKey: 'compass.welcomeShort', tagKey: 'compass.welcomeTag',
    primaryHsl: null, accentHsl: null, accentCss: '#F4BE52',
    workflowId: null,
  },
  {
    id: 'corrido', labelKey: 'compass.corrido', shortKey: 'compass.corridoShort', tagKey: 'compass.corridoTag',
    primaryHsl: null, accentHsl: null, accentCss: '#F4BE52',
    workflowId: 'corrido',
  },
  {
    id: 'story', labelKey: 'compass.story', shortKey: 'compass.storyShort', tagKey: 'compass.storyTag',
    // #25F4EE ≈ hsl(178 91% 55%), #FE2C55 ≈ hsl(348 99% 58%)
    // Every themed stop carries its own headline gradient (2026-09-24): with
    // none, the gradient lines kept corrido's amber-to-teal on a cyan page.
    primaryHsl: '178 91% 55%', accentHsl: '348 99% 58%', accentCss: '#25F4EE',
    gradient: 'linear-gradient(135deg, #25F4EE, #FE2C55)',
    workflowId: 'tiktok_story',
  },
  {
    id: 'ads', labelKey: 'compass.ads', shortKey: 'compass.adsShort', tagKey: 'compass.adsTag',
    // #22c55e ≈ hsl(152 76% 52%), gold accent hsl(45 93% 58%)
    primaryHsl: '152 76% 52%', accentHsl: '45 93% 58%', accentCss: '#22c55e',
    gradient: 'linear-gradient(135deg, hsl(152 76% 52%), hsl(45 93% 58%))',
    workflowId: 'ads',
  },
  {
    // The storybook's own coral (9 74% 46%) is only 3.9:1 on this dark page,
    // so the stop uses a lighter coral: 7.1:1 on the page, 6.0:1 on a glass
    // card, and 7.3:1 for the near-black button text on top of it. The sunny
    // yellow accent is the storybook palette's own (12.4:1 on the page).
    id: 'storybook', labelKey: 'compass.storybook', shortKey: 'compass.storybookShort', tagKey: 'compass.storybookTag',
    primaryHsl: '9 90% 68%', accentHsl: '45 95% 62%', accentCss: '#F77A64',
    gradient: 'linear-gradient(135deg, hsl(9 90% 72%), hsl(340 82% 76%) 50%, hsl(45 95% 64%))',
    workflowId: null,
  },
  {
    // ChopBox, the free photo and video editor (2026-09-29): its own stop,
    // in the editor's teal (#4FC5CD, the ChopBox nav picture's ring) with the
    // site amber as the accent.
    id: 'chopbox', labelKey: 'compass.chopbox', shortKey: 'compass.chopboxShort', tagKey: 'compass.chopboxTag',
    primaryHsl: '184 56% 56%', accentHsl: '40 88% 64%', accentCss: '#4FC5CD',
    gradient: 'linear-gradient(135deg, hsl(184 56% 62%), hsl(40 88% 64%))',
    workflowId: null,
  },
  {
    id: 'rap', labelKey: 'compass.rap', shortKey: 'compass.rapShort', tagKey: 'compass.rapTag',
    primaryHsl: '258 90% 76%', accentHsl: '258 30% 40%', accentCss: '#a78bfa',
    gradient: 'linear-gradient(135deg, hsl(258 90% 76%), hsl(280 70% 72%))',
    comingSoon: true, workflowId: null,
  },
];

export const isToolId = (id: unknown): id is ToolId =>
  typeof id === 'string' && TOOLS.some((t) => t.id === id);

/** Index of a stop in TOOLS. Anything unknown resolves to the welcome stop
 *  (index 0) instead of -1, because TOOLS[-1] is undefined and the hero reads
 *  its theme straight off the result — one bad id used to take down the app. */
export const toolIndex = (id: unknown) => {
  const i = TOOLS.findIndex((t) => t.id === id);
  return i === -1 ? 0 : i;
};

export const getTool = (id: unknown): ToolDef => TOOLS[toolIndex(id)];

// A press has to travel this far before it counts as a swipe (and before the
// compass takes pointer capture for it), so ordinary taps stay taps.
const DRAG_SLOP = 8;
const SWIPE_THRESHOLD = 55;

type Swipe = { x: number; id: number; active: boolean; captured: boolean };
const IDLE_SWIPE: Swipe = { x: 0, id: -1, active: false, captured: false };

export function ToolCompass({ tool, onChange }: { tool: ToolId; onChange: (t: ToolId) => void }) {
  const { t, i18n } = useTranslation();
  // The story studio (2026-10-03): its stop says "soon" until the server can reach the story service.
  const storyAvailable = useStoryAvailability();
  const soon = (item: ToolDef) => !!item.comingSoon || (item.id === 'story' && !storyAvailable);
  const idx = toolIndex(tool);
  const active = TOOLS[idx];
  const prev = idx > 0 ? TOOLS[idx - 1] : null;
  const next = idx < TOOLS.length - 1 ? TOOLS[idx + 1] : null;

  // Plain pointer-based swipe (no framer-motion drag — its gesture layer was
  // swallowing the button clicks so taps never switched the tool, 2026-07-22).
  const swipe = useRef<Swipe>({ ...IDLE_SWIPE });
  // Right after a swipe switches the tool, the click the browser may fire at
  // the end of that same drag must not switch it a second time. Time-boxed,
  // because a touch swipe usually ends with no click at all.
  const swallowClickUntil = useRef(0);
  const focusActiveAfterKey = useRef(false);
  const activeLabelRef = useRef<HTMLButtonElement>(null);

  const go = (dir: -1 | 1) => {
    const to = Math.min(TOOLS.length - 1, Math.max(0, idx + dir));
    if (to !== idx) onChange(TOOLS[to].id);
  };

  // Pointer capture keeps the pointerup coming to us when a drag leaves the
  // pill. It is taken on pointerdown only for a press on the pill itself; a
  // press on a button is captured once it has moved like a swipe, because a
  // captured tap has its click re-targeted away from the button it hit.
  const capture = (e: PointerEvent<HTMLDivElement>) => {
    if (swipe.current.captured) return;
    try {
      e.currentTarget.setPointerCapture?.(e.pointerId);
      swipe.current.captured = true;
    } catch { /* pointer already gone; the swipe still works without capture */ }
  };
  const resetSwipe = () => { swipe.current = { ...IDLE_SWIPE }; };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    focusActiveAfterKey.current = true;
    go(e.key === 'ArrowRight' ? 1 : -1);
  };

  // After an arrow key, whatever had focus no longer marks where you are (a
  // neighbour label now names a different stop, an arrow may have just gone
  // disabled and dropped focus to <body>), so focus follows the active label.
  useEffect(() => {
    if (!focusActiveAfterKey.current) return;
    focusActiveAfterKey.current = false;
    activeLabelRef.current?.focus();
  }, [idx]);

  // NEVER CLIP A LABEL: on sm+ the active stop sits centred with its two
  // neighbours dimmed either side. Five stops no longer fit in one row, and a
  // long Spanish name can still outgrow its side column at the narrowest
  // widths, so a neighbour that doesn't fit whole is hidden rather than cut.
  const prevColRef = useRef<HTMLDivElement>(null);
  const nextColRef = useRef<HTMLDivElement>(null);
  const [fits, setFits] = useState({ prev: true, next: true });
  const measure = useCallback(() => {
    const fitsIn = (col: HTMLDivElement | null) => {
      const label = col?.firstElementChild as HTMLElement | null;
      if (!col || !label) return true;
      return label.offsetWidth <= col.clientWidth + 0.5;
    };
    const nextFits = { prev: fitsIn(prevColRef.current), next: fitsIn(nextColRef.current) };
    setFits((cur) => (cur.prev === nextFits.prev && cur.next === nextFits.next ? cur : nextFits));
  }, []);
  // ONE LINE, ONE HEIGHT (2026-09-24 review): on a phone the active stop
  // used to wrap its full name onto two or three lines ('Videos de Historias
  // / para TikTok'), so the pill grew and the headline jumped on every swipe.
  // Now the pill never wraps: when the full name doesn't fit on one line it
  // shows the stop's short name (the one the neighbour labels use) and keeps
  // the full name as its accessible name and in the live region. A hidden
  // probe measures the full name; sm+ always has room for it.
  const labelCellRef = useRef<HTMLDivElement>(null);
  const probeRef = useRef<HTMLSpanElement>(null);
  const [useShort, setUseShort] = useState(false);
  const measureLabel = useCallback(() => {
    const probe = probeRef.current;
    const btn = activeLabelRef.current;
    const cell = labelCellRef.current;
    if (!probe || !btn || !cell || typeof window === 'undefined') return;
    let short = false;
    const wide = typeof window.matchMedia === 'function' && window.matchMedia('(min-width: 640px)').matches;
    if (!wide && cell.clientWidth > 0 && probe.offsetWidth > 0) {
      const cs = getComputedStyle(btn);
      // padding + the gap + the 6px beacon
      const chrome = parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight) + (parseFloat(cs.columnGap) || 0) + 6;
      short = probe.offsetWidth + chrome > cell.clientWidth + 0.5;
    }
    setUseShort((cur) => (cur === short ? cur : short));
  }, []);
  useLayoutEffect(() => { measure(); measureLabel(); }, [measure, measureLabel, idx, i18n.language]);
  // The web fonts change every width above, and the first paint uses the
  // fallback font. The observers below catch most of it; these make sure the
  // pill settles on its final name the moment the fonts are in (2026-09-25).
  useEffect(() => {
    const fonts = typeof document !== 'undefined' ? document.fonts : undefined;
    if (!fonts) return undefined;
    let alive = true;
    const again = () => { if (alive) { measure(); measureLabel(); } };
    void fonts.ready?.then(again).catch(() => undefined);
    fonts.addEventListener?.('loadingdone', again);
    return () => {
      alive = false;
      fonts.removeEventListener?.('loadingdone', again);
    };
  }, [measure, measureLabel]);
  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return;
    // Columns resize with the active label; the labels themselves resize when
    // the web fonts arrive. Either can change what fits.
    const ro = new ResizeObserver(() => { measure(); measureLabel(); });
    [prevColRef.current, nextColRef.current].forEach((col) => {
      if (!col) return;
      ro.observe(col);
      if (col.firstElementChild) ro.observe(col.firstElementChild);
    });
    if (labelCellRef.current) ro.observe(labelCellRef.current);
    if (probeRef.current) ro.observe(probeRef.current);
    return () => ro.disconnect();
  }, [measure, measureLabel, idx]);

  // The active label eases in when the stop CHANGES, never on arrival
  // (2026-09-25): on a busy phone the entrance sat at its opacity-0 start
  // for seconds, so the pill showed only its dot. Derived during render, so
  // the very first frame of a switch already carries the class.
  const [seenIdx, setSeenIdx] = useState(idx);
  const [labelEnters, setLabelEnters] = useState(false);
  if (idx !== seenIdx) {
    setSeenIdx(idx);
    setLabelEnters(true);
  }

  const soonWord = (_item: ToolDef) => t('compass.soon');
  const chipFor = (item: ToolDef) => (
    <span className="ml-2 inline-block rounded-full border border-white/20 px-1.5 py-px align-[0.12em] font-sans text-[11px] font-bold uppercase leading-[1.35] tracking-[0.12em] text-white/60">{soonWord(item)}</span>
  );
  const activeName = `${t(active.labelKey)}${soon(active) ? ` (${soonWord(active)})` : ''}`;

  // THE DOCK (immersive restyle, 2026-09-24): a floating glass pill with a
  // gradient hairline tinted by the active stop, 44px arrows, the active
  // stop as a lit capsule with a beacon, and 44px-tall dots. Visual only:
  // every handler, testid and aria attribute is unchanged, except that the
  // active label now names its full stop in aria-label (see ONE LINE above).
  const accent = active.accentCss;

  const neighbour = (item: ToolDef | null, fit: boolean) => item && (
    <button
      type="button"
      onClick={() => onChange(item.id)}
      data-testid={`compass-${item.id}`}
      className={[
        'min-h-[44px] shrink-0 whitespace-nowrap rounded-full px-3 text-[0.8rem] font-semibold tracking-wide text-white/45 transition-colors duration-300 hover:bg-white/[0.06] hover:text-white/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40',
        fit ? '' : 'invisible',
      ].join(' ')}
    >
      {t(item.shortKey)}
      {soon(item) && chipFor(item)}
    </button>
  );

  // Pixel sizes, not h-11: the phone root font is 15px, where 2.75rem is 41px.
  // An arrow with nowhere to go is hidden but keeps its box, so the pill
  // stays centred (2026-09-25): a greyed chevron on the welcome stop read as
  // a disabled carousel. It stays disabled too, for keyboards and tests.
  const arrow = 'flex h-[44px] w-[44px] shrink-0 items-center justify-center rounded-full text-white/60 transition duration-300 hover:bg-white/10 hover:text-white active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 disabled:pointer-events-none disabled:invisible';

  return (
    <div
      className="mx-auto mb-2 flex w-full max-w-[34rem] select-none flex-col items-center sm:mb-5 md:max-w-2xl lg:max-w-[36rem] xl:max-w-2xl"
      data-testid="tool-compass"
      role="group"
      aria-label={t('compass.ariaLabel')}
      onKeyDown={onKeyDown}
    >
      <div
        className="hero-hairline relative flex w-full items-center justify-between gap-1 rounded-full bg-[linear-gradient(180deg,rgba(28,31,38,0.72),rgba(12,14,18,0.6))] p-1 shadow-[0_18px_40px_-18px_rgba(0,0,0,0.85),inset_0_1px_0_rgba(255,255,255,0.06)] [@media(hover:hover)_and_(pointer:fine)]:backdrop-blur-md"
        style={{
          touchAction: 'pan-y',
          ['--hairline' as string]: `linear-gradient(120deg, rgba(255,255,255,0.3), rgba(255,255,255,0.06) 38%, ${accent}99)`,
        }}
        data-testid="tool-compass-track"
        onPointerDown={(e) => {
          swallowClickUntil.current = 0;
          swipe.current = { x: e.clientX, id: e.pointerId, active: true, captured: false };
          if (!(e.target as Element | null)?.closest?.('button')) capture(e);
        }}
        onPointerMove={(e) => {
          if (!swipe.current.active || e.pointerId !== swipe.current.id) return;
          if (Math.abs(e.clientX - swipe.current.x) > DRAG_SLOP) capture(e);
        }}
        onPointerUp={(e) => {
          if (!swipe.current.active || e.pointerId !== swipe.current.id) return;
          const dx = e.clientX - swipe.current.x;
          resetSwipe();
          if (Math.abs(dx) > SWIPE_THRESHOLD) swallowClickUntil.current = Date.now() + 500;
          if (dx < -SWIPE_THRESHOLD) go(1);        // swipe left -> next tool
          else if (dx > SWIPE_THRESHOLD) go(-1);   // swipe right -> previous
        }}
        // The browser took the gesture over (a vertical page scroll on touch)
        // or the pointer vanished: forget the half-finished swipe entirely.
        onPointerCancel={resetSwipe}
        onClickCapture={(e) => {
          if (Date.now() > swallowClickUntil.current) return;
          swallowClickUntil.current = 0;
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        <button
          type="button" aria-label={t('compass.previousTool')} onClick={() => go(-1)} disabled={idx === 0}
          className={arrow}
        >
          <ChevronLeft className="h-[1.1rem] w-[1.1rem]" />
        </button>
        <div ref={labelCellRef} className="grid min-w-0 flex-1 grid-cols-1 items-center sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] sm:gap-1">
          {/* Nothing before the first stop (2026-09-25): the faint 'Start
              here' that used to sit here repeated the headline right under
              it and made the welcome dock read as a disabled carousel. */}
          <div ref={prevColRef} className="hidden min-w-0 justify-end overflow-hidden sm:flex" data-testid="compass-prev-slot">
            {neighbour(prev, fits.prev)}
          </div>
          <button
            ref={activeLabelRef}
            type="button"
            onClick={() => onChange(active.id)}
            data-testid={`compass-${active.id}`}
            aria-current="true"
            aria-label={activeName}
            className="relative mx-auto flex min-h-[44px] max-w-full items-center justify-center gap-2 whitespace-nowrap rounded-full px-4 py-1.5 text-center font-display text-[1rem] font-bold leading-tight tracking-[0.01em] text-foreground transition-[background,box-shadow] duration-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50 max-[359px]:px-3 max-[359px]:text-[0.94rem] sm:px-5 sm:text-[0.92rem]"
            style={{
              background: `linear-gradient(180deg, ${accent}30, ${accent}0d)`,
              boxShadow: `inset 0 0 0 1px ${accent}66, 0 0 30px -6px ${accent}80, 0 10px 24px -14px rgba(0,0,0,0.8)`,
            }}
          >
            <span aria-hidden="true" className="hero-beacon relative h-1.5 w-1.5 shrink-0 rounded-full transition-colors duration-500" style={{ background: accent }} />
            {/* Keyed on the stop only: a re-measure (fonts, language) that
                swaps the full name for the short one changes the text in
                place instead of replaying the entrance. */}
            <span key={active.id} className={`min-w-0 truncate${labelEnters ? ' hero-label-in' : ''}`}>
              {useShort ? t(active.shortKey) : t(active.labelKey)}
              {soon(active) && chipFor(active)}
            </span>
            {/* Measures the full name on one line; never shown, and clipped
                to a 0x0 box so it can't widen the page on a small phone. */}
            <span aria-hidden="true" className="pointer-events-none invisible absolute left-0 top-0 h-0 w-0 overflow-hidden">
              <span ref={probeRef} className="inline-block whitespace-nowrap">
                {t(active.labelKey)}
                {soon(active) && chipFor(active)}
              </span>
            </span>
          </button>
          <div ref={nextColRef} className="hidden min-w-0 justify-start overflow-hidden sm:flex" data-testid="compass-next-slot">
            {neighbour(next, fits.next)}
          </div>
        </div>
        <button
          type="button" aria-label={t('compass.nextTool')} onClick={() => go(1)} disabled={idx === TOOLS.length - 1}
          className={arrow}
        >
          <ChevronRight className="h-[1.1rem] w-[1.1rem]" />
        </button>
      </div>
      {/* position dots — which page of the compass you're on. Each dot keeps
          its h-6 w-6 box but grows to a 44px-tall, 40px-wide tap target
          around the same small visual dot (min-size beats size). */}
      {/* Phones: the 44px targets reach up into the track's padding and down
          into the space below, so the row costs ~30px instead of ~52px. */}
      <div className="hero-compass-dots -mb-3 -mt-1 flex items-center sm:-mb-1.5 sm:mt-0.5">
        {TOOLS.map((item) => {
          const on = item.id === active.id;
          return (
            <button
              key={item.id} type="button" aria-label={t(item.labelKey)} onClick={() => onChange(item.id)}
              aria-current={on ? 'true' : undefined}
              data-testid={`compass-dot-${item.id}`}
              className="group flex h-6 w-6 min-h-[44px] min-w-[40px] items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
            >
              <span
                className={`block h-1.5 rounded-full transition-all duration-500 ease-expo-out ${on ? '' : 'bg-white/25 group-hover:bg-white/60'}`}
                style={on ? { width: 22, background: item.accentCss, boxShadow: `0 0 12px ${item.accentCss}aa` } : { width: 6 }}
              />
            </button>
          );
        })}
      </div>
      <span className="sr-only" aria-live="polite" data-testid="compass-live">
        {t('compass.nowShowing', { tool: t(active.labelKey) })}
      </span>
    </div>
  );
}
